import { useRouter } from "next/router";

import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { useEffect, useMemo } from "react";
import { inferSSRProps } from "types/inferSSRProps";
import { ConnectorJobPostStatus } from "types/models";
import { UserRole } from "types/userRole";

import { trackEvent } from "components/analytics-wrapper/analytics-wrapper";
import { JobPostDisplay } from "components/job-posts/job-post-display";
import HeadSeo from "components/layout/head-seo";
import Layout from "components/layout/layout";
import { Button, NoticeBar, ShareButton } from "components/ui";
import { SkeletonDisplay } from "components/ui/skeleton";

import { SEO_IMG_OGIMG } from "@/lib/constants";
import { useApp } from "@/lib/context/app-context";
import { ERROR_TYPES } from "@/lib/errors";
import * as fbq from "@/lib/fb-pixel";
import { useCopy } from "@/lib/hooks/use-copy";
import { BreadcrumbsSchema } from "@/lib/schema/breadcrumbs";
import { JobPostingSchema } from "@/lib/schema/job-posting";
import { buildJobPostKeywords } from "@/lib/seo/keywords";
import { constructJobPostImage } from "@/lib/seo/og-images";
import { isJobPostAdmin } from "@/lib/user-helpers";

import ZettlorService from "services/zettlor-service";

type PageProps = inferSSRProps<typeof getStaticProps>;

const zettlorService = new ZettlorService();
export default function JobPostPublicViewPage({ jobPost }: PageProps) {
  const router = useRouter();
  const { user } = useApp();
  const copy = useCopy();

  useEffect(() => {
    if (jobPost) {
      fbq.event(fbq.FbPixelEventType.ViewContent, {
        content_name: jobPost.title,
        content_ids: [jobPost.uuid],
      });
    }
  }, [jobPost]);

  const { data: connectorJobPostData } = useQuery({
    queryKey: ["connectorJobPost", router.query.uuid],
    queryFn: () => zettlorService.connectorJobPostLookup(jobPost?.uuid as string),
    enabled: !!jobPost && user?.selected_role === UserRole.Connector,
  });
  const connectorJobPost = connectorJobPostData?.data;

  useEffect(() => {
    if (user?.organizations.find((o) => o.uuid === jobPost?.organization.uuid)) return;
    trackEvent("Job Post Viewed", {
      organization_id: jobPost?.organization.uuid,
      job_post_id: jobPost?.uuid,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasConnectorJobPost = useMemo(() => {
    return (
      user?.selected_role === UserRole.Connector &&
      connectorJobPost &&
      connectorJobPost?.status === ConnectorJobPostStatus.OPEN &&
      user?.uuid === connectorJobPost.connector.uuid
    );
  }, [connectorJobPost, user]);

  const hasOfferPending = useMemo(() => {
    return (
      user?.selected_role === UserRole.Connector &&
      connectorJobPost &&
      connectorJobPost?.status === ConnectorJobPostStatus.OFFERED &&
      user?.uuid === connectorJobPost.connector.uuid
    );
  }, [connectorJobPost, user]);

  const noticeBar = () => {
    if (isJobPostAdmin(user, jobPost)) {
      return (
        <NoticeBar
          label="You are an admin for this job."
          actions={
            <div className="flex items-center justify-end space-x-2">
              <ShareButton
                variant="secondary"
                object="job post"
                title={jobPost.title}
                url={`${process.env.NEXT_PUBLIC_SHORT_LINK_BASE_URL}/${jobPost.short_id}`}
                text="Share job"
              />
              <Button variant="secondary" href={`/job-posts/${jobPost.uuid}/edit`}>
                Edit
              </Button>
            </div>
          }
        />
      );
    } else if (hasConnectorJobPost) {
      return (
        <NoticeBar
          label={copy.connector_job_post_exists_notice}
          actions={
            <Button variant="secondary" href={`/${connectorJobPost.connector.handle}/${connectorJobPost.uuid}`}>
              View
            </Button>
          }
        />
      );
    } else if (hasOfferPending) {
      return (
        <NoticeBar
          label={copy.connector_job_post_offer_pending}
          actions={
            <Button variant="secondary" href="/connector-job-posts">
              View contracts
            </Button>
          }
        />
      );
    }

    return null;
  };

  return (
    <Layout noticeBar={noticeBar()} showFooter>
      <HeadSeo
        title={`${jobPost?.title} job at ${jobPost?.organization.name}`}
        description={`${format(new Date(), "MMM dd, yyyy")} - Submit candidates to ${jobPost?.organization.name} ${
          jobPost?.title
        } job on Zettlor and get paid. View job description, responsibilities and qualifications.`}
        pageImage={
          SEO_IMG_OGIMG +
          constructJobPostImage({
            jobPostTitle: jobPost?.title,
            bidContractValue: jobPost?.contract?.placement_contract?.value?.toString(),
            organizationName: jobPost?.organization.name,
            organizationLogo: jobPost?.organization.logo,
          })
        }
        keywords={jobPost ? buildJobPostKeywords([jobPost]) : []}
      />
      <JobPostingSchema jobPost={jobPost} />
      <BreadcrumbsSchema
        items={[
          {
            position: 1,
            name: `${jobPost?.organization.name}'s jobs`,
            item: `${process.env.NEXT_PUBLIC_BASE_URL}/o/@${jobPost?.organization.handle}`,
          },
          {
            position: 2,
            name: jobPost?.title,
            item: `${process.env.NEXT_PUBLIC_BASE_URL}/job-posts/${jobPost?.uuid}`,
          },
        ]}
      />

      {!!jobPost ? (
        <JobPostDisplay
          jobPost={jobPost}
          organization={jobPost.organization}
          hasActiveConnectorJobPost={hasConnectorJobPost}
        />
      ) : (
        <SkeletonDisplay />
      )}
    </Layout>
  );
}

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: "blocking",
  };
  if (process.env.NEXT_PUBLIC_NODE_ENV !== "production") {
    return {
      paths: [],
      fallback: "blocking",
    };
  }

  const zettlorService = new ZettlorService();
  const { data: jobPosts } = await zettlorService.listJobPostsSSG({ limit: 100 });

  return {
    paths: jobPosts?.results
      ? jobPosts?.results.map((c) => {
          return {
            params: {
              uuid: c.uuid,
            },
          };
        })
      : [],
    fallback: "blocking",
  };
}

export async function getStaticProps({ params }) {
  const zettlorService = new ZettlorService();
  const queryUuid = params.uuid;
  const errorPath = `/job-posts/${queryUuid}`;

  const { data: jobPost } = await zettlorService.getJobPost(queryUuid);
  if (!jobPost) {
    return {
      redirect: {
        destination: `${ERROR_TYPES.job_post_not_found.url}&errorPath=${errorPath}`,
        permanent: false,
      },
    };
  }

  return {
    props: {
      jobPost,
    },
    revalidate: 10,
  };
}
